import React from 'react';
import { normalizeProductKey, ShopProductForm } from '../utils/premiumBundleRules';

type Props = {
  form: ShopProductForm;
  onChange: (patch: Partial<ShopProductForm>) => void;
  /** Edit mode: ProductKey + Environment identify the record and can't change. */
  isUpdateMode: boolean;
  /** Bundles may leave ThemePreference unset so each component keeps its own. */
  themePreferenceHelp: string;
};

const inputClass = 'w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white disabled:opacity-60';
const Label: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <label className='block text-xs text-gray-400 mb-1'>{children}</label>
);

/** The catalog fields every purchasable Themes & Decor product shares (identity, price, availability, status). */
const ShopProductFields: React.FC<Props> = ({ form, onChange, isUpdateMode, themePreferenceHelp }) => (
  <div className='space-y-4'>
    <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
      <div>
        <Label>ProductKey *</Label>
        <input
          value={form.ProductKey}
          disabled={isUpdateMode}
          onChange={(e) => onChange({ ProductKey: normalizeProductKey(e.target.value) })}
          className={inputClass}
          placeholder='Spaces auto-replaced with underscores'
        />
      </div>
      <div>
        <Label>Name *</Label>
        <input value={form.Name} onChange={(e) => onChange({ Name: e.target.value })} className={inputClass} />
      </div>
      <div className='sm:col-span-2'>
        <Label>Description</Label>
        <input value={form.Description} onChange={(e) => onChange({ Description: e.target.value })} className={inputClass} />
      </div>
      <div>
        <Label>Environment *</Label>
        <select
          value={form.Environment}
          disabled={isUpdateMode}
          onChange={(e) => onChange({ Environment: e.target.value as 'dev' | 'prod' })}
          className={inputClass}
        >
          <option value='prod' className='bg-[#1E1E2D]'>Production (prod)</option>
          <option value='dev' className='bg-[#1E1E2D]'>Development (dev)</option>
        </select>
        <p className='text-[11px] text-gray-500 mt-1'>Components must exist in the same environment.</p>
      </div>
      <div>
        <Label>CategoryKey *</Label>
        <input value={form.CategoryKey} onChange={(e) => onChange({ CategoryKey: e.target.value.trim().toUpperCase() })} className={inputClass} />
        <p className='text-[11px] text-gray-500 mt-1'>THEMES_DECOR shows it under Wallet Shop → Themes &amp; Decor.</p>
      </div>
      <div>
        <Label>Theme Preference</Label>
        <select
          value={form.ThemePreference}
          onChange={(e) => onChange({ ThemePreference: e.target.value as ShopProductForm['ThemePreference'] })}
          className={inputClass}
        >
          <option value='' className='bg-[#1E1E2D]'>Not set</option>
          <option value='adaptive' className='bg-[#1E1E2D]'>Adaptive (Matches app theme)</option>
          <option value='light' className='bg-[#1E1E2D]'>Light (Always use light version)</option>
          <option value='dark' className='bg-[#1E1E2D]'>Dark (Always use dark version)</option>
        </select>
        <p className='text-[11px] text-gray-500 mt-1'>{themePreferenceHelp}</p>
      </div>
      <div>
        <Label>DisplayOrder</Label>
        <input type='number' min='0' value={form.DisplayOrder} onChange={(e) => onChange({ DisplayOrder: e.target.value })} className={inputClass} />
      </div>
      <div className='sm:col-span-2'>
        <Label>Tags (comma separated)</Label>
        <input value={form.Tags} onChange={(e) => onChange({ Tags: e.target.value })} className={inputClass} placeholder='premium, bundle' />
      </div>
    </div>

    <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
      <div>
        <Label>Currency Type *</Label>
        <div className='flex gap-2'>
          {(['coin', 'token'] as const).map((ct) => (
            <button
              key={ct}
              type='button'
              onClick={() => onChange({ CurrencyType: ct })}
              className={`flex-1 py-2 text-xs font-semibold rounded-lg border transition ${
                form.CurrencyType === ct
                  ? ct === 'token'
                    ? 'bg-purple-500/30 border-purple-400/60 text-purple-200'
                    : 'bg-amber-500/30 border-amber-400/60 text-amber-200'
                  : 'bg-white/5 border-white/20 text-gray-400 hover:border-white/40'
              }`}
            >
              {ct === 'coin' ? '🪙 Coins' : '💎 Tokens'}
            </button>
          ))}
        </div>
      </div>
      {form.CurrencyType === 'coin' ? (
        <div>
          <Label>Price (Coins) *</Label>
          <input type='number' min='1' value={form.PriceCoins} onChange={(e) => onChange({ PriceCoins: e.target.value })} className={inputClass} />
        </div>
      ) : (
        <div>
          <Label>Price (Tokens) *</Label>
          <input type='number' min='1' value={form.PriceTokens} onChange={(e) => onChange({ PriceTokens: e.target.value })} className={inputClass} />
        </div>
      )}
      <div>
        <Label>Availability *</Label>
        <select
          value={form.AvailabilityType}
          onChange={(e) => onChange({ AvailabilityType: e.target.value as 'unlimited' | 'limited' })}
          className={inputClass}
        >
          <option value='unlimited' className='bg-[#1E1E2D]'>Unlimited</option>
          <option value='limited' className='bg-[#1E1E2D]'>Limited</option>
        </select>
      </div>
      {form.AvailabilityType === 'limited' && (
        <div>
          <Label>Quantity Available *</Label>
          <input
            type='number'
            min='0'
            value={form.QuantityAvailable}
            onChange={(e) => onChange({ QuantityAvailable: e.target.value })}
            className={inputClass}
          />
        </div>
      )}
      <div>
        <Label>Available From</Label>
        <input
          type='datetime-local'
          value={form.AvailabilityStartAt}
          onChange={(e) => onChange({ AvailabilityStartAt: e.target.value })}
          className={inputClass}
        />
      </div>
      <div>
        <Label>Available Until</Label>
        <input
          type='datetime-local'
          value={form.AvailabilityEndAt}
          onChange={(e) => onChange({ AvailabilityEndAt: e.target.value })}
          className={inputClass}
        />
      </div>
      <div>
        <Label>Per-User Limit</Label>
        <input type='number' min='1' value={form.PerUserLimit} onChange={(e) => onChange({ PerUserLimit: e.target.value })} className={inputClass} />
      </div>
      <div>
        <Label>Max Purchase Per Order</Label>
        <input
          type='number'
          min='1'
          value={form.MaxPurchasePerOrder}
          onChange={(e) => onChange({ MaxPurchasePerOrder: e.target.value })}
          className={inputClass}
        />
      </div>
    </div>

    <div className='flex flex-wrap items-center gap-6'>
      <label className='flex items-center gap-2 cursor-pointer text-xs text-gray-200'>
        <input
          type='checkbox'
          checked={form.IsFeatured}
          onChange={(e) => onChange({ IsFeatured: e.target.checked })}
          className='w-4 h-4 accent-[#FF9C15]'
        />
        Featured
      </label>
      <div className='flex items-center gap-4'>
        <span className='text-xs text-gray-400'>Status *</span>
        <label className='flex items-center gap-2 cursor-pointer text-xs text-gray-200'>
          <input type='radio' checked={form.IsActive} onChange={() => onChange({ IsActive: true })} className='accent-[#FF9C15]' />
          Active <span className='text-emerald-400 font-semibold'>(Live in shop)</span>
        </label>
        <label className='flex items-center gap-2 cursor-pointer text-xs text-gray-200'>
          <input type='radio' checked={!form.IsActive} onChange={() => onChange({ IsActive: false })} className='accent-[#FF9C15]' />
          Inactive <span className='text-amber-400 font-semibold'>(Draft / Hidden)</span>
        </label>
      </div>
    </div>
  </div>
);

export default ShopProductFields;
